export interface AnalyticsConfig {
  gtmId?: string;
}

interface AnalyticsHead {
  script: Record<string, unknown>[];
  noscript: Record<string, unknown>[];
}

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/;
const GTM_ORIGIN = 'https://www.googletagmanager.com';

// IDs are interpolated into an inline script, so anything not matching the expected format is dropped.
export function sanitizeAnalyticsConfig(config: AnalyticsConfig): Required<AnalyticsConfig> {
  const gtmId = (config.gtmId || '').trim();

  return {
    gtmId: GTM_ID_PATTERN.test(gtmId) ? gtmId : '',
  };
}

/**
 * Builds the head tags for Google Tag Manager. GTM is the only Google tag loaded by the app;
 * GA4 and any other tags are configured inside the GTM container.
 *
 * SPA page views are sent by the GA4 tag's enhanced measurement ("page changes based on
 * browser history events"), so no manual page_view is pushed on route change here.
 */
export function buildAnalyticsHead(config: AnalyticsConfig): AnalyticsHead | null {
  const { gtmId } = sanitizeAnalyticsConfig(config);
  if (!gtmId) return null;

  const init = [
    'window.dataLayer=window.dataLayer||[];',
    "window.dataLayer.push({'gtm.start':new Date().getTime(),event:'gtm.js'});",
  ];

  return {
    // Unhead weights async scripts at 30 and inline scripts at 50, so pin the init below 30 to run first.
    script: [
      { key: 'analytics-init', innerHTML: init.join(''), tagPriority: 25 },
      { key: 'gtm', src: `${GTM_ORIGIN}/gtm.js?id=${gtmId}`, async: true },
    ],
    noscript: [
      {
        key: 'gtm-noscript',
        tagPosition: 'bodyOpen',
        innerHTML: `<iframe src="${GTM_ORIGIN}/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
      },
    ],
  };
}
